import { forwardRef } from "react"
import { cn } from "../../lib/utils"
import type { ButtonHTMLAttributes } from "react"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "destructive" | "outline" | "ghost" | "link"
    size?: "default" | "sm" | "lg" | "icon"
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "primary", size = "default", ...props }, ref) => {
        return (
            <button
                className={cn(
                    "cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
                    {
                        // Primary - Red background with hover effect
                        "bg-red-600 text-white shadow hover:bg-red-700": variant === "primary",

                        // Destructive - Similar to primary but with a different color
                        "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90": variant === "destructive",

                        // Secondary/Outline - Just a border
                        "border border-red-600/30 bg-transparent text-white hover:bg-red-600/10":
                            variant === "secondary" || variant === "outline",

                        // Ghost - No background or border, only text hover effect
                        "bg-transparent text-white hover:text-red-400": variant === "ghost",

                        // Link style
                        "text-primary underline-offset-4 hover:underline p-0 h-auto": variant === "link",

                        // Sizes
                        "h-8 px-4 py-1 text-sm": size === "default",
                        "h-6 rounded-md px-3 text-sm": size === "sm",
                        "h-10 rounded-md px-8 text-md": size === "lg",
                        "h-9 w-9 p-0": size === "icon",
                    },
                    className,
                )}
                ref={ref}
                {...props}
            />
        )
    },
)
Button.displayName = "Button"

export { Button }
